import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Badge, Button, Card, Popconfirm, Popover, Space, Table, Typography, type TableProps } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import type { FieldDefinitionDto, OrganizationListItem, RuleGroup } from '@crm/shared';
import { emptyRuleGroup } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { useListParams } from '../../components/list-params';
import { FilterBuilder, FilterSummary } from '../../components/FilterBuilder';
import { ColumnChooser, SortableColumns, draggableHeader, orderedFields, sortableHeaderComponents, useVisibleColumns } from '../../components/ColumnChooser';
import { formatValue } from '../../components/DynamicField';
import { downloadCsv } from '../../components/export-csv';
import { ExportButton } from '../../components/ExportButton';
import { readValue } from '../../features/fields/system-keys';
import { useFields } from '../../features/fields/api';
import { contactName } from '../../features/contacts/api';
import { useDeleteOrganizations, useOrganizations } from '../../features/organizations/api';
import { errorMessage, fileUrl } from '../../api/client';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { plural } from '../../utils/format';
import { OrganizationDrawer } from './OrganizationDrawer';
import { NameCell, TableSearch } from '../../components/NameCell';
import { LinksView } from '../../components/LinksField';
import { EntityTag } from '../../components/EntityTag';
import { TABLE_NAME_CHARS } from '../contacts/ContactsPage';

export function OrganizationsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { params, setParams, filterGroup, setFilterGroup } = useListParams({ sortBy: 'created_at', sortDir: 'desc' });
  const defs = useFields('ORGANIZATION');
  const orgs = useOrganizations(params);
  const remove = useDeleteOrganizations();
  const { visible, setVisible } = useVisibleColumns('organizations.columns', defs.data);
  const [selected, setSelected] = useState<string[]>([]);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState<RuleGroup>(filterGroup ?? emptyRuleGroup());
  const [filterOpen, setFilterOpen] = useState(false);

  const sortOrder = (key: string) => (params.sortBy === key ? (params.sortDir === 'asc' ? ('ascend' as const) : ('descend' as const)) : null);

  const columns = useMemo<TableProps<OrganizationListItem>['columns']>(() => {
    const d = defs.data ?? [];
    return [
      { title: 'Name', key: 'name', fixed: 'left' as const, sorter: true, sortOrder: sortOrder('name'), render: (_: unknown, o: OrganizationListItem) => <NameCell name={o.name} sub={o.domain ?? o.industry} to={`/organizations/${o.id}`} image={fileUrl(o.logoFileId)} square /> },
      ...orderedFields(d, visible)
        .filter((f) => f.key !== 'name')
        .map((f: FieldDefinitionDto) => ({ title: f.label, key: f.key, onHeaderCell: () => draggableHeader(f.key), ellipsis: true, sorter: true, sortOrder: sortOrder(f.key), render: (_: unknown, o: OrganizationListItem) => renderOrgCell(f, o) })),
      {
        title: 'Main contact',
        key: 'main_contact',
        ellipsis: true,
        render: (_: unknown, o: OrganizationListItem) => (o.mainContact ? <EntityTag kind="contact" id={o.mainContact.id} maxChars={TABLE_NAME_CHARS}>{contactName(o.mainContact)}</EntityTag> : <Typography.Text type="secondary">—</Typography.Text>),
      },
      { title: 'Contacts', key: 'contact_count', sorter: true, sortOrder: sortOrder('contact_count'), render: (_: unknown, o: OrganizationListItem) => o.contactCount },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defs.data, visible, params.sortBy, params.sortDir]);

  const onChange: TableProps<OrganizationListItem>['onChange'] = (pagination, _f, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setParams({
      page: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 25,
      sortBy: s?.order ? (s.columnKey as string) : undefined,
      sortDir: s?.order ? (s.order === 'ascend' ? 'asc' : 'desc') : undefined,
    });
  };

  return (
    <div className="page">
      <PageHeader
        title="Organizations"
        subtitle={orgs.data ? plural(orgs.data.total, 'organization') : undefined}
        extra={
          <Space>
            <Button onClick={() => navigate('/tools/imports/new')}>Import</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer(true)}>New organization</Button>
          </Space>
        }
      />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={params.q} onSearch={(q) => setParams({ q: q || undefined })} placeholder="Search name, domain, industry" />
          <FilterSummary group={filterGroup} fields={defs.data ?? []} />
        </div>
        <div className="toolbar-group">
        <Popover
          open={filterOpen}
          onOpenChange={(o) => { setFilterOpen(o); if (o) setDraft(filterGroup ?? emptyRuleGroup()); }}
          trigger="click"
          placement="bottomLeft"
          content={
            <div>
              <FilterBuilder value={draft} onChange={setDraft} contactFields={defs.data ?? []} objectType="ORGANIZATION" />
              <Space style={{ marginTop: 12 }}>
                <Button type="primary" size="small" onClick={() => { setFilterGroup(draft); setFilterOpen(false); }}>Apply</Button>
                <Button size="small" onClick={() => { setDraft(emptyRuleGroup()); setFilterGroup(undefined); setFilterOpen(false); }}>Clear</Button>
              </Space>
            </div>
          }
        >
          <Badge count={filterGroup?.conditions.length ?? 0} size="small">
            <Button icon={<FilterOutlined />}>Filter</Button>
          </Badge>
        </Popover>
        <ColumnChooser defs={(defs.data ?? []).filter((f) => f.key !== 'name')} visible={visible} onChange={setVisible} />
        {selected.length > 0 && (
          <Popconfirm
            title={`Delete ${plural(selected.length, 'organization')}? Their contacts are kept.`}
            okText="Delete"
            okButtonProps={{ danger: true, loading: remove.isPending }}
            onConfirm={() => remove.mutateAsync(selected).then(() => { message.success(`Deleted ${plural(selected.length, 'organization')}`); setSelected([]); }).catch((e) => message.error(errorMessage(e)))}
          >
            <Button danger>Delete {selected.length}</Button>
          </Popconfirm>
        )}
        <ExportButton onExport={() => downloadCsv('/organizations/export.csv', params)} count={orgs.data?.total} noun="organization" />
        </div>
      </div>
      <SortableColumns visible={visible} onChange={setVisible}>
      <Table<OrganizationListItem>
        components={sortableHeaderComponents}
        rowKey="id"
        size="middle"
        loading={tableLoading(orgs)}
        dataSource={orgs.data?.items ?? []}
        columns={columns}
        locale={tableLocale(
          orgs,
          params.q || filterGroup?.conditions.length ? (
            <EmptyState description="No organizations match this search or filter." action={<Button onClick={() => { setParams({ q: undefined }); setFilterGroup(undefined); }}>Clear search and filters</Button>} />
          ) : (
            <EmptyState description="No organizations yet. They are also created when contacts are imported with a company." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer(true)}>New organization</Button>} />
          ),
        )}
        scroll={{ x: 'max-content' }}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
        onChange={onChange}
        pagination={{ current: params.page, pageSize: params.pageSize, total: orgs.data?.total ?? 0, showSizeChanger: true, showTotal: (t) => `${t.toLocaleString()} total` }}
        onRow={(o) => ({ onDoubleClick: () => navigate(`/organizations/${o.id}`) })}
      />
      </SortableColumns>
      </Card>
      <OrganizationDrawer open={drawer} onClose={() => setDrawer(false)} defs={defs.data ?? []} onSaved={(o) => navigate(`/organizations/${o.id}`)} />
    </div>
  );
}

export function renderOrgCell(f: FieldDefinitionDto, o: OrganizationListItem) {
  const v = readValue(f, o as unknown as Record<string, unknown>);
  if (f.type === 'LINKS') return <LinksView value={v} inline />;
  if ((f.type === 'URL' || f.key === 'domain') && v) {
    const href = String(v).startsWith('http') ? String(v) : `https://${String(v)}`;
    return <a href={href} target="_blank" rel="noreferrer">{String(v)}</a>;
  }
  return formatValue(f, v);
}
